import React, { useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { MultiSelect, type Option } from 'react-multi-select-component'

import { TagElement } from '../../../components/Tag/Tag'
import { useOutsideClick } from '../../../common/useOutsideClick'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetAllUsersQuery } from '../../../store/user/user'

import { sortUserOptionsBySelected, UserOption, userToUserOption } from './utils'

import './Collaboration.scss'

interface CheckboxItemRendererProps {
  checked: boolean
  option: UserOption
  disabled?: boolean
  onClick: () => void
}

interface UserMultiSelectorProps {
  excludeUserId?: string
  selectedUserIds: string[]
  onSelectedUsersChange: (userIds: string[]) => void
  noSelectedUserText: string
  isCreatable?: boolean
  allowedUserIds?: string[]
}

const ClearIcon = <i className='bi bi-x-lg' />

export const AccountUserMultiSelector: React.FC<UserMultiSelectorProps> = ({
  excludeUserId,
  selectedUserIds,
  onSelectedUsersChange,
  noSelectedUserText,
  isCreatable = true,
  allowedUserIds,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: usersData } = useGetAllUsersQuery({ accountId, identityTypes: ['idp', 'cross-account'] })

  const [selectedUserIdsSnapshot, setSelectedUserIdsSnapshot] = useState<string[]>(selectedUserIds)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => {
    setIsOpen(false)
    setSelectedUserIdsSnapshot(selectedUserIds)
  })

  const userOptions = useMemo<UserOption[]>(
    () =>
      (usersData?.data ?? [])
        .filter((user) => !!user.email && user.id !== excludeUserId && (!allowedUserIds || allowedUserIds.includes(user.id)))
        .map(userToUserOption),
    [excludeUserId, usersData, allowedUserIds],
  )

  const sortedUserOptions = useMemo(() => sortUserOptionsBySelected(userOptions, selectedUserIdsSnapshot), [userOptions, selectedUserIdsSnapshot])

  const selectedUserOptions = userOptions.filter((u) => selectedUserIds.includes(u.value))

  const customValueRenderer = (selected: Option[]) =>
    selected.length ? `${selected.length} user${selected.length > 1 ? 's' : ''} selected` : noSelectedUserText

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isOpen) {
      return
    }
    setIsOpen(true)
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen((prev) => !prev)
  }

  const ArrowRenderer = ({ expanded }: { expanded: boolean }) => (
    <div onClick={handleToggle}>{expanded ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}</div>
  )

  const CheckboxItemRenderer = ({ checked, option, onClick, disabled }: CheckboxItemRendererProps) => (
    <div className={`item-renderer ${disabled ? 'disabled' : ''}`}>
      <Form.Check
        disabled={disabled || false}
        key={option.key}
        type='checkbox'
        id={`option_${option.key}`}
        label={
          <>
            {option.value !== '' ? (
              <>
                <strong>{option.label}</strong> - <small>{option.email}</small>
                {option.crossAccount ? <TagElement className='user-share-user-tag ms-2'>CROSS-ACCOUNT</TagElement> : null}
              </>
            ) : (
              option.label
            )}
          </>
        }
        checked={checked}
        onChange={onClick}
        value={option.value}
      />
    </div>
  )

  return (
    <div ref={containerRef} onClick={handleOpen}>
      <MultiSelect
        isOpen={isOpen}
        hasSelectAll={false}
        options={sortedUserOptions}
        value={selectedUserOptions}
        onChange={(selected: UserOption[]) => onSelectedUsersChange(selected.map((u) => u.value))}
        labelledBy='Select'
        isCreatable={isCreatable}
        valueRenderer={customValueRenderer}
        ItemRenderer={CheckboxItemRenderer}
        ArrowRenderer={ArrowRenderer}
        ClearSelectedIcon={ClearIcon}
        overrideStrings={{ noOptions: 'You do not have any teammates.', selectAll: 'Select/Deselect All' }}
      />
    </div>
  )
}
